import React, { useEffect } from 'react'
import { useDrag } from 'react-dnd'
import { IArpMedia } from '../../../../../features/arp-registry/ArpMedia'
import { styled } from '../../../../lib/stitches'
import { useTreeState } from '../../../../stores/Root'
import { StemIcon } from '../../../core/icons/icons'
import LoadingAnimation from '../../../core/LoadingAnimation'
import { gray1, gray6 } from '../../../platform/color'
import { StudioDraggableTypes } from '../../StudioUtils'
import useSampleListItem from './useSampleListItem'

const StyledListItem = styled('li', {
  display: 'flex',
  alignItems: 'center',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  marginLeft: '12px',
  height: '28px',
  textOverflow: 'ellipsis',
  maxWidth: 'calc(100%)',
  '&:hover': {
    color: gray1,
    cursor: 'pointer'
  }
})

interface Props {
  sampleData: IArpMedia
  index: number
  selectedIndex: number
  highlightedIndex: number
  setHighlightedIndex: (index: number) => void
  setSelectedIndex: (index: number) => void
  onClick?: () => void
  isActive: boolean
}

const SampleListItem: React.FC<Props> = ({
  sampleData,
  index,
  selectedIndex,
  highlightedIndex,
  setHighlightedIndex,
  setSelectedIndex,
  onClick,
  isActive
}) => {
  const { isLoading, isPlaying, playSample } = useSampleListItem(sampleData)
  const isStem = sampleData.metadata.bpm !== 0 || sampleData.metadata.mediaType === 'stem'
  const item = isStem
    ? { type: StudioDraggableTypes.STEM, stem: sampleData }
    : { type: StudioDraggableTypes.SAMPLE, sample: sampleData.id }
  const [, drag] = useDrag(() => ({
    type: StudioDraggableTypes.SAMPLE,
    collect: (monitor) => ({
      isDragging: !!monitor.isDragging()
    }),
    item
  }))
  const { studio } = useTreeState()

  // Handle selection, on enter press
  useEffect(() => {
    if (selectedIndex === index) {
      playSample().then(() => setSelectedIndex(-1))
    }
  }, [selectedIndex, index])

  // Handle up/down arrow keys
  useEffect(() => {
    if (highlightedIndex === index && isActive) {
      playSample()
    }
  }, [highlightedIndex, index, isActive])

  const handleClick = async () => {
    if (index === highlightedIndex) {
      setSelectedIndex(index)
    } else {
      setHighlightedIndex(index)
    }

    if (onClick) {
      onClick()
    }
  }

  const isHighlighted = index === highlightedIndex

  return (
    <StyledListItem
      onClick={handleClick}
      onDoubleClick={() => studio.addNewStemForSample(sampleData.id)}
      ref={drag}
      css={{ color: isHighlighted && isActive ? gray1 : gray6 }}
      title={sampleData.metadata.title}
    >
      <div className="pe-1">
        {isLoading ? (
          <LoadingAnimation light small />
        ) : (
          <>
            {isStem && !isPlaying ? (
              <StemIcon width="16px" height="16px" />
            ) : (
              <img
                alt="Play state"
                src={`/static/icons/studio_${isPlaying ? 'pause' : 'play'}_circle.svg`}
              />
            )}
          </>
        )}
      </div>
      {sampleData.metadata.title}
    </StyledListItem>
  )
}

export default SampleListItem
